import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import {Store} from '@ngxs/store';
import {cloneDeep, get} from 'lodash';
import {
  ConfigurationState,
  InventoryService,
  PostingDataResponseModel,
  EditPostingState,
  UpdatePosting,
  EditPostingFormNames,
  PostingTypes,
  LaunchDarklyService
} from 'remarketing-angular-library';
import {take, takeUntil} from 'rxjs/operators';
import {PostingFormDirective} from '../posting-form/posting-form.directive';
import { PostingAnalyticsHelperService } from '../../services/posting-analytics-helper.service';
import {
  MultiSelectorDropdownComponent
} from '../../multi-selector-dropdown/multi-selector-dropdown.component';
import { CharacterCountComponent, CharacterCountDirective } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { SellerDisclosureModalComponent } from '../seller-disclosure-modal/seller-disclosure-modal.component';

@Component({
    selector: 'app-condition-form',
    templateUrl: './condition-form.component.html',
    styleUrls: ['./condition-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        MultiSelectorDropdownComponent,
        CharacterCountComponent,
        CommonModule,
        CharacterCountDirective,
        SellerDisclosureModalComponent
    ]
})
export class ConditionFormComponent extends PostingFormDirective implements OnInit {
  @Output() yellowLightEvent = new EventEmitter<boolean>();
  @Input() postingTypeString: string;
  @ViewChild(SellerDisclosureModalComponent) sellerDisclosureModal: SellerDisclosureModalComponent;
  postingData: PostingDataResponseModel;
  conditionsForm: UntypedFormGroup;
  yellowLights: any[];
  characterCountData: any;
  postingType: PostingTypes;

  translations = {
    textCondition:'',
    textApplyVehicle:'',
    ariaLabelLearnRedLight:'',
    ariaLabelRedLight:'',
    ariaLabelLearnYellowLight:'',
    ariaLabelYellowLight:'',
    ariaLabelNonOperable:'',
    labelAdditionalConditionDisclosures:'',
    ariaLabelSellerComments:''
  }

  constructor(
    private readonly store: Store,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly translocoService: TranslocoService,
    private readonly inventoryService: InventoryService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly _launchDarklyService: LaunchDarklyService
  ) {
    super();
  }

  get isMultipleYellowLightsEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-yellowLightMultiSelectEnabled', true);
  }

    get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  checkedListOnChange($event) {
    this.yellowLights = $event;
    this.updateYellowLightsOnPostingData();
  }

  updateYellowLightsOnPostingData() {
    const updatedPostingData = {
      ...this.postingData,
      inventoryItem: {
        ...this.postingData.inventoryItem,
        disclosures: {
          ...this.postingData.inventoryItem.disclosures,
          yellowLights: this.yellowLights
        }
      }
    };
    this.store.dispatch(new UpdatePosting(updatedPostingData));
  }

  public restrictSomeChars() {
    const formFieldValue = this.conditionsForm.get('sellerComments').value;
    const formattedValue = formFieldValue.replace(/[~";^|]/g, '');
    this.conditionsForm.patchValue({
      sellerComments: formattedValue
    });
  }

  ngOnInit() {
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(data => {
        this.postingData = cloneDeep(data);
      });

    this.prepareYellowLightDropdownOptions();

    this.buildForm();
    this.sendPageloadAnalyticsEvent();
    this.getTranslations();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.DISCLOSURES_CONDITION
    );
  }

  prepareYellowLightDropdownOptions(): void {
    this.store
      .select(EditPostingState.postingType)
      .pipe(take(1))
      .subscribe(postingType => {
        this.postingType = postingType;
      });

    const yellowLightsInState = this.postingData?.inventoryItem?.disclosures?.yellowLights;
    if (this.postingType === PostingTypes.NEW && (!yellowLightsInState || yellowLightsInState.length === 0)) {
      this.displayYellowLightOptionsForNewPosting();
    } else {
      this.displayYellowLightOptionsForExistingPosting();
    }
  }

  displayYellowLightOptionsForNewPosting() {
    this.inventoryService
      .getDisclosures()
      .pipe(take(1))
      .subscribe(
        yellowLightDtos => {
          // Response does not include the selected property and 2.0 will throw errors if not present.
          yellowLightDtos?.forEach(yellowLight => (yellowLight.selected = false));
          this.yellowLights = yellowLightDtos;
          this.yellowLights = this.sortDisclosureDescriptions();
        },
        error => {
          // Future error handling here pending UX decision
        },
        () => {
          this.updateYellowLightsOnPostingData();
        }
      );
  }

  displayYellowLightOptionsForExistingPosting() {
    this.yellowLights = this.postingData.inventoryItem.disclosures.yellowLights;
    this.yellowLights = this.sortDisclosureDescriptions();
  }

  ngOnDestroy() {
    // if no yellow light selection was made in drop down.
    // persist unchecked yellow light to parent, because checkbox is unchecked when navigating away
    if (!this.isYellowLightSelected) {
      this.yellowLightEvent.emit(false);
    }
  }

  public sortDisclosureDescriptions() {
    return this.yellowLights?.sort((a, b) => {
      if (a.disclosureDescription.toUpperCase() < b.disclosureDescription.toUpperCase()) {
        return -1;
      } else {
        return a.disclosureDescription.toUpperCase() > b.disclosureDescription.toUpperCase() ? 1 : 0;
      }
    });
  }

  buildForm() {
    this.conditionsForm = this.formBuilder.group({
      redLight: this.isRedLight,
      yellowLights: this.isYellowLightSelected,
      nonOperable: this.isNonOperable,
      sellerComments: this.inputSellerComments
    });
  }

  get isRedLight(): boolean {
    return get(this.postingData, 'inventoryItem.disclosures.arbitrationFlag', false);
  }

  onRedLightChange($event) {
    this.updateRedLightOnChange($event.target.checked);
  }

  updateRedLightOnChange(isChecked: boolean) {
    const updatedPostingData = {
      ...this.postingData,
      inventoryItem: {
        ...this.postingData.inventoryItem,
        disclosures: {
          ...this.postingData.inventoryItem.disclosures,
          arbitrationFlag: isChecked
        }
      }
    };
    this.store.dispatch(new UpdatePosting(updatedPostingData));
  }

  get isYellowLightSelected(): boolean {
    return this.yellowLights ? this.yellowLights.some(light => light.selected === true) : false;
  }

  get isYellowLightChecked() {
    const element: HTMLInputElement = document.getElementById('yellowLights') as HTMLInputElement;
    return element.checked;
  }

  clearYellowLightsSelections() {
    const yellowLights = this.postingData.inventoryItem.disclosures.yellowLights;
    yellowLights.forEach(yellowlight => {
      yellowlight.selected = false;
    });

    const updatedPostingData = {
      ...this.postingData,
      inventoryItem: {
        ...this.postingData.inventoryItem,
        disclosures: {
          ...this.postingData.inventoryItem.disclosures,
          yellowLights: yellowLights
        }
      }
    };
    this.store.dispatch(new UpdatePosting(updatedPostingData));
    this.yellowLights = yellowLights;
  }

  onYellowLightChange($event) {
    if (!$event.target.checked) {
      this.clearYellowLightsSelections();
    }
    this.yellowLightEvent.emit($event.target.checked); // send yellow light checked status to parent component
  }

  get isNonOperable(): boolean {
    return get(this.postingData, 'inventoryItem.disclosures.nonOperableFlag', false);
  }

  updateNonOperableLightOnChange(isChecked: boolean) {
    const updatedPostingData = {
      ...this.postingData,
      inventoryItem: {
        ...this.postingData.inventoryItem,
        disclosures: {
          ...this.postingData.inventoryItem.disclosures,
          nonOperableFlag: isChecked
        }
      }
    };
    this.store.dispatch(new UpdatePosting(updatedPostingData));
  }

  onNonOperableLightChange($event) {
    this.updateNonOperableLightOnChange($event.target.checked);
  }

  get inputSellerComments(): string {
    return get(this.postingData, 'inventoryItem.disclosures.sellerComments', '');
  }

  updateSellerCommentsOnChange() {
    const comments = this.conditionsForm.get('sellerComments').value;
    const updatedPostingData = {
      ...this.postingData,
      inventoryItem: {
        ...this.postingData.inventoryItem,
        disclosures: {
          ...this.postingData.inventoryItem.disclosures,
          sellerComments: comments
        }
      }
    };
    this.store.dispatch(new UpdatePosting(updatedPostingData));
  }

  getTranslations(): void {
    const keyParams = {
      'sellModule.posting.conditionForm': {},
      'UIComponents': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.translations.textCondition = translate[0].textCondition;
        this.translations.textApplyVehicle = translate[0].textApplyVehicle;
        this.translations.ariaLabelLearnRedLight = translate[0].ariaLabelLearnRedLight;
        this.translations.ariaLabelRedLight = translate[0].ariaLabelRedLight;
        this.translations.ariaLabelLearnYellowLight = translate[0].ariaLabelLearnYellowLight;
        this.translations.ariaLabelYellowLight = translate[0].ariaLabelYellowLight;
        this.translations.ariaLabelNonOperable = translate[0].ariaLabelNonOperable;
        this.translations.labelAdditionalConditionDisclosures = translate[0].labelAdditionalConditionDisclosures;
        this.translations.ariaLabelLearnRedLight = translate[0].ariaLabelLearnRedLight;
        this.translations.ariaLabelLearnRedLight = translate[0].ariaLabelLearnRedLight;
        this.translations.ariaLabelSellerComments = translate[0].ariaLabelSellerComments;
        this.characterCountData = translate[1].characterCount;
      });
  }
}
